import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { Router } from '@angular/router';
import moment from 'moment';
import { AccountType, UserRole } from 'src/app/enums/user-role';

import { SharedService } from '../../../layouts/shared-service';
import { accountTypeChangedEvent, fboChangedEvent } from '../../../constants/sharedEvents';
import { FbopricesService } from '../../../services/fboprices.service';
// Services
import { FbosService } from '../../../services/fbos.service';
import { ManageConfirmationComponent } from '../../../shared/components/manage-confirmation/manage-confirmation.component';
import { PricingExpiredNotificationGroupComponent } from '../../../shared/components/pricing-expired-notification-group/pricing-expired-notification-group.component';
// Components
import { FbosDialogNewFboComponent } from '../fbos-dialog-new-fbo/fbos-dialog-new-fbo.component';
import { FbosGridNewFboDialogComponent } from '../fbos-grid-new-fbo-dialog/fbos-grid-new-fbo-dialog.component';
import { localStorageAccessConstant } from 'src/app/constants/LocalStorageAccessConstant';
import { GroupsService } from 'src/app/services/groups.service';
import { ManageFboGroupsService } from 'src/app/services/managefbo.service';
import { Subscription } from 'rxjs';
import { FboUiService } from 'src/app/ui-services/fbos/fbo-ui.service';
import { FboBusinessService } from 'src/app/business/fbo/fbo-business.service';
import { PermissionsService } from 'src/app/services/security/permissions.service';
import { AppDialogWrapperService } from '../../../ni-components/app-dialog-wrapper/app-dialog-wrapper.service';

@Component({
    selector: 'app-fbos-grid',
    styleUrls: ['./fbos-grid.component.scss'],
    templateUrl: './fbos-grid.component.html',
})
export class FbosGridComponent implements OnInit {
    // Input/Output Bindings
    @Output() editFboClicked = new EventEmitter<any>();
    @Input() fbosData: Array<any>;
    @Input() groupInfo: any;

    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;

    // Public Members
    public pageTitle = 'FBOs';
    public fbosDataSource: MatTableDataSource<any> = null;
    public displayedColumns: string[] = ['icao', 'fbo', 'active', 'manage'];
    public airportData: Array<any>;
    public resultsLength = 0;
    public canManageFbo = false;
    public isDeleting: boolean;
    public searchValue = '';

    public pageIndexFbos = 0;
    public pageSizeFbos = 25;

    public tableSortFbos = 'icao';
    public tableSortOrderFbos = 'asc';

    sortChangeSubscription: Subscription;
    constructor(
        private fboService: FbosService,
        private fboPricesService: FbopricesService,
        private sharedService: SharedService,
        private snackBar: MatSnackBar,
        private router: Router,
        private groupsService: GroupsService,
        private manageFboGroupsService: ManageFboGroupsService,
        private fboUiService: FboUiService,
        private fboBusinessService: FboBusinessService,
        private permissions: PermissionsService,
        private appDialogWrapperService: AppDialogWrapperService
    ) {
        
        this.canManageFbo = [UserRole.Conductor, UserRole.GroupAdmin].includes(
            this.sharedService.currentUser.role
        );

        if (this.sharedService.currentUser.role === UserRole.Conductor) {
            this.displayedColumns = [
                'icao',
                'fbo',
                'price',
                'active',
                'edit',
                'delete',
            ];
        } else {
            this.displayedColumns = ['icao', 'fbo', 'price', 'active', 'edit'];
        }
    }

    isPremiumAccount(fbo) {
        return fbo.accountType == AccountType.Premium;
    }

    isFreemiumAccount(fbo) {
        return fbo.accountType == AccountType.Freemium;
    }

    async ngOnInit() {
        if (!this.fbosData) {
            return;
        }
        this.sortChangeSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
        this.refreshTable();
        if (this.sharedService.currentUser.role !== 3) {
            const remindMeLaterFlag = localStorage.getItem(
                'pricingExpiredNotification'
            );
            const noThanksFlag = sessionStorage.getItem(
                'pricingExpiredNotification'
            );
            if (noThanksFlag) {
                return;
            }

            if (
                remindMeLaterFlag &&
                moment(new Date(moment().format('L'))) <=
                    moment(new Date(remindMeLaterFlag))
            ) {
                return;
            }

            this.checkExistingPrices();
        }

        this.paginator.pageIndex = 0;
    }
    ngOnDestory() {
        this.sortChangeSubscription?.unsubscribe();
    }
    public deactivateAccount(record) {
        this.fboUiService.deactivateAccount(record);
    }

    public activateAccount(record) {
        this.fboUiService.activateAccount(record);
    }

    public editRecord(record) {
        this.editFboClicked.emit(record);
    }

    public newRecord() {
        var groupId = 0;
        if (this.sharedService.isManagingGroup())
            groupId = this.sharedService.currentUser.groupId;
        else if (this.groupInfo)
            groupId = this.groupInfo.oid;
        if (groupId > 0) {
            const dialogWrapper = this.appDialogWrapperService.open({
                title: 'New FBO',
                component: FbosDialogNewFboComponent,
                width: '450px',
                data: {
                    groupId: groupId,
                    initialSetupPhase: true,
                },
            });

            dialogWrapper.afterClosed().subscribe((result) => {
                if (result) {
                    this.fbosData.push(result);

                    this.refreshTable();
                    this.snackBar.open(result.fbo + ' is created', '', {
                        duration: 3000,
                        panelClass: ['blue-snackbar'],
                    });
                    sessionStorage.setItem('isNewFbo', 'yes');
                    this.editRecord(result);
                }
            });
        } else {
            const dialogWrapper = this.appDialogWrapperService.open({
                title: 'New FBO',
                component: FbosGridNewFboDialogComponent,
                width: '450px',
                data: {},
            });

            dialogWrapper.afterClosed().subscribe((result) => {
                if (result) {
                    this.fbosData.push(result);
                    this.refreshTable();
                    this.snackBar.open(result.fbo + ' is created', '', {
                        duration: 3000,
                        panelClass: ['blue-snackbar'],
                    });
                    sessionStorage.setItem('isNewFbo', 'yes');
                    this.router.navigate([
                        '/default-layout/fbos/' + result.oid,
                    ]);
                }
            });
        }
    }

    public refreshTable() {
        this.fbosDataSource = new MatTableDataSource(this.fbosData);
        this.fbosDataSource.sort = this.sort;
        this.fbosDataSource.paginator = this.paginator;
        this.resultsLength = this.fbosData.length;
    }

    public applyFilter(filterValue: string) {
        this.fbosDataSource.filter = filterValue.trim().toLowerCase();
    }

    public manageFBO(fbo, $event) {
        if (
            ($event !== null &&
                ($event.srcElement.nodeName.toLowerCase() === 'button' ||
                    $event.srcElement.nodeName.toLowerCase() === 'select' ||
                    ($event.srcElement.nodeName.toLowerCase() === 'input' &&
                        $event.srcElement.getAttribute('type') ===
                        'checkbox'))) ||
            !this.canManageFbo
        ) {
            $event.stopPropagation();
            return;
        }

        const dialogWrapper = this.appDialogWrapperService.open({
            title: 'Manage FBO?',
            component: ManageConfirmationComponent,
            width: '450px',
            hideHeader: true,
            data: {
                description:
                    'This will temporarily switch your account to a primary user for this FBO.  Would you like to continue?',
                fboId: fbo.oid,
                title: 'Manage FBO?',
            },
        });

        dialogWrapper.afterClosed().subscribe(async (result) => {
            if (!result) {
                return;
            }

            if (this.sharedService.currentUser.role != 3) {
                this.fboService.updateLastLogin(fbo.oid).subscribe((data: any) => {
                    this.completeManageProcess(fbo);
                });
            }
            else {
                let isNetworkFbo = this.manageFboGroupsService.getIsNetworkFboSearchByFboList(this.fbosData);
                this.fboBusinessService.logIntoFboAccount(fbo, isNetworkFbo, this.sharedService.isConductorUser);
            }
        });
    }
    async completeManageProcess(fbo) {
        localStorage.setItem(
            localStorageAccessConstant.managerGroupId,
            this.sharedService.currentUser.groupId.toString()
        );
        this.sharedService.currentUser.managerGroupId =
            this.sharedService.currentUser.groupId;

        localStorage.setItem(localStorageAccessConstant.groupId, fbo.groupId.toString());
        this.sharedService.currentUser.groupId = fbo.groupId;

        localStorage.setItem(localStorageAccessConstant.impersonatedrole, '1');
        this.sharedService.currentUser.impersonatedRole = 1;

        localStorage.setItem(localStorageAccessConstant.fboId, fbo.oid.toString());
        this.sharedService.currentUser.fboId = fbo.oid;

        this.sharedService.currentUser.icao = fbo.icao;

        this.sharedService.setCurrentUserPropertyValue(localStorageAccessConstant.accountType, fbo.accountType);

        this.sharedService.setCurrentUserPropertyValue(
            localStorageAccessConstant.isNetworkFbo,
            this.manageFboGroupsService
                .getIsNetworkFboSearchByFboList(this.fbosData)
                .toString()
        );

        var isSingleSourceFbo = await this.groupsService.isGroupFboSingleSource(fbo.icao).toPromise();

        this.sharedService.setCurrentUserPropertyValue(localStorageAccessConstant.isSingleSourceFbo, isSingleSourceFbo.toString());

        this.sharedService.emitChange(fboChangedEvent);
        this.sharedService.emitChange(accountTypeChangedEvent);

        this.permissions.setIsFboSelected(true);

        this.router.navigate(['/default-layout/dashboard-fbo-updated/']);
    }

    public checkExistingPrices() {
        this.fboPricesService
            .checkFboExpiredPricingGroup(this.sharedService.currentUser.groupId)
            .subscribe((data: any) => {
                if (this.sharedService.currentUser.role != 6 && this.sharedService.currentUser.fboId > 0 && data && data.length) {
                    const dialogWrapper = this.appDialogWrapperService.open({
                        title: 'Pricing Expired',
                        component: PricingExpiredNotificationGroupComponent,
                        data,
                    });
                    dialogWrapper.afterClosed().subscribe((result) => {
                        if (result && result.fboId) {
                            this.sharedService.currentUser.impersonatedRole = 1;
                            this.sharedService.currentUser.fboId = result.fboId;
                            localStorage.setItem(
                                localStorageAccessConstant.fboId,
                                this.sharedService.currentUser.fboId.toString()
                            );
                            this.sharedService.emitChange(fboChangedEvent);
                            this.router.navigate([
                                '/default-layout/dashboard-fbo-updated/',
                            ]);
                        }
                    });
                }
            });
    }
}
